import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import PageTitle from 'Components/PageTitle'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import ParameterFormFields from 'Services/ProductManagement/Features/Parameter/Components/ParameterFormFields'
import { SELECTED_PARAMETER_KEY } from 'Services/ProductManagement/Features/Parameter/Hooks/useParameterGroups'
import {
  useGetSingleParameterGroupQuery,
  useUpdateSingleParameterGroupMutation
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type {
  ParameterGroup,
  ParameterGroupParameter
} from 'Services/ProductManagement/Features/Parameter/types'
import type { ParameterTranslation } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { PRODUCT_MANAGEMENT_ROOT_URL, PTA_PARAM_KEY, TAB_PARAM_KEY } from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, DialogContentText, Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

function ParameterEditPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/product-parameters`
  const navigate = useNavigate()

  const { parameterPta, parameterGroupCategory, parameterId } = useParams()
  const previousLocation = usePreviousLocation(
    `${baseUrl}?${PTA_PARAM_KEY}=${parameterPta}&${TAB_PARAM_KEY}=${parameterGroupCategory}`
  )

  const pageTitle = t`Edit parameter`

  const { data: singleParameterGroup = null, isLoading } = useGetSingleParameterGroupQuery({
    category: parameterGroupCategory as string,
    pta: parameterPta as string
  })
  const [updateParameterGroup, { isLoading: isSubmitLoading }] =
    useUpdateSingleParameterGroupMutation()

  let parameterGroupParameters = singleParameterGroup ? singleParameterGroup.parameters : []

  const parameterGroupParameter = singleParameterGroup?.parameters.find(
    parameter => parameter.parameter_id === parameterId
  )
  const initialParameterGroupParameterDetails: ParameterGroupParameter = {
    parameter_name: '',
    parameter_id: '',
    translations: [],
    minimum: null,
    maximum: null
  }
  const [parameterGroupParameterDetails, setParameterGroupParameterDetails] =
    useState<ParameterGroupParameter>(initialParameterGroupParameterDetails)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (singleParameterGroup && parameterGroupParameter) {
      setParameterGroupParameterDetails(parameterGroupParameter)
    }
  }, [parameterGroupParameter])

  const breadcrumbs = [
    {
      title: t`Settings`
    },
    {
      title: t`Product parameters`,
      link: baseUrl
    },
    {
      title: singleParameterGroup
        ? getTranslation(singleParameterGroup.translations, 'fi').value
        : '',
      link: `${baseUrl}#${parameterGroupCategory}`
    },
    {
      title: parameterGroupParameter
        ? getTranslation(parameterGroupParameter.translations, 'fi').value
        : ''
    }
  ]

  const setDetails = (
    value: string | number | ParameterTranslation[] | null,
    name: keyof ParameterGroupParameter
  ) => {
    setParameterGroupParameterDetails(prevState => ({ ...prevState, [name]: value }))
  }

  /**
   * Update a parameter group parameter in the array based on a matching parameterId.
   */
  const updateParameterGroupParameters = () => {
    // Find the index of the parameter group parameter with the matching parameterId
    const foundParameterGroupParameterIndex = parameterGroupParameters.findIndex(
      parameterGroupParameter => parameterGroupParameter.parameter_id === parameterId
    )

    if (foundParameterGroupParameterIndex !== -1) {
      // Update the parameter group parameter at the found index with parameterGroupParameterDetails
      parameterGroupParameters = parameterGroupParameters.map((parameterGroupParameter, index) =>
        index === foundParameterGroupParameterIndex
          ? parameterGroupParameterDetails
          : parameterGroupParameter
      )
    }
  }

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!singleParameterGroup) {
      SnackbarUtils.error(t`There was an error editing the parameter.`)
      return
    }
    updateParameterGroupParameters()
    const updatedParameterGroup: ParameterGroup = {
      ...singleParameterGroup,
      parameters: parameterGroupParameters
    }
    const params = {
      parameterGroupName: parameterGroupCategory as string,
      payload: updatedParameterGroup
    }
    updateParameterGroup(params)
      .unwrap()
      .then(() => {
        setIsSaveDialogOpen(false)
        navigate(
          appendQueryParamsToUrl(previousLocation, {
            [SELECTED_PARAMETER_KEY]: parameterGroupParameterDetails.parameter_id
          })
        )
        SnackbarUtils.success(t`Parameter successfully updated.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error editing the parameter.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={previousLocation} />
          <PageTitle>{pageTitle}</PageTitle>
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <WithLoader loading={isLoading}>
            <Box>
              <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
                <ParameterFormFields
                  parameterGroupParameter={parameterGroupParameterDetails}
                  handleSetDetails={setDetails}
                  isEdit={true}
                />
                <SaveDialog
                  open={isSaveDialogOpen}
                  handleClose={() => setIsSaveDialogOpen(false)}
                  handleSubmit={handleSubmit}
                  loading={isSubmitLoading}
                  title={t`Save parameter`}
                >
                  <DialogContentText>
                    <Typography component="span" color="text.primary">
                      {t`Are you sure you want to save the parameter?`}
                    </Typography>
                  </DialogContentText>
                </SaveDialog>
                <CustomLoadingButton
                  isLoading={isSubmitLoading}
                  onClick={() => setIsSaveDialogOpen(true)}
                  size="large"
                  iconPosition="end"
                >
                  {t`Save parameter`}
                </CustomLoadingButton>
              </StyledForm>
            </Box>
          </WithLoader>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ParameterEditPage
